// Default to public Base44 hostname; override via VITE_BASE44_API_URL if needed.
const BASE_URL = (import.meta.env.VITE_BASE44_API_URL as string) || 'https://base44.app/api'
const DEFAULT_APP_ID = '68a9ceb78a3b2f5c335c3a70'
const DEFAULT_API_KEY = '2d6f94f0d2824fac95e1e9decb4bbb98'
const APP_ID = (import.meta.env.VITE_BASE44_APP_ID as string) || DEFAULT_APP_ID
const API_KEY = (import.meta.env.VITE_BASE44_API_KEY as string) || DEFAULT_API_KEY

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'

async function request<T>(path: string, opts: { method?: Method; body?: any; params?: unknown } = {}) {
  const { method = 'GET', body, params } = opts
  const url = new URL(`${BASE_URL}${path}`)
  if (params && typeof params === 'object' && !Array.isArray(params)) {
    Object.entries(params as Record<string, any>).forEach(([k, v]) => {
      if (v === undefined || v === null) return
      url.searchParams.set(k, String(v))
    })
  }
  const res = await fetch(url.toString(), {
    method,
    headers: {
      'api_key': API_KEY,
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    },
    body: body ? JSON.stringify(body) : undefined,
    credentials: 'omit'
  })
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(`Base44 ${method} ${url.pathname}: ${res.status} ${res.statusText} ${text}`)
  }
  // 204 No Content
  if (res.status === 204) return undefined as unknown as T
  return (await res.json()) as T
}

export function entityClient<T = any>(entityName: string) {
  const appId = APP_ID || DEFAULT_APP_ID
  const base = `/apps/${appId}/entities/${entityName}`
  return {
    async list(params?: unknown): Promise<T[]> {
      try {
        // Support string shorthand for sorting. Base44 expects `sort`.
        const normalizedParams = typeof params === 'string' ? { sort: params } : params
        const data = await request<any>(base, { params: normalizedParams })
        if (Array.isArray(data)) return data as T[]
        if (data && Array.isArray((data as any).results)) return (data as any).results as T[]
        return []
      } catch (e) {
        console.warn(`Base44 list failed for ${entityName}:`, e)
        return []
      }
    },
    async get(id: string): Promise<T | null> {
      try {
        return await request<T>(`${base}/${id}`)
      } catch (e) {
        console.warn(`Base44 get failed for ${entityName}:${id}`, e)
        return null
      }
    },
    async filter(params: Record<string, any>): Promise<T[]> {
      return await this.list(params)
    },
    async create(body: Partial<T>): Promise<T> {
      return await request<T>(base, { method: 'POST', body })
    },
    async update(id: string, body: Partial<T>): Promise<void> {
      // Base44 expects full updates via PUT
      await request<void>(`${base}/${id}`, { method: 'PUT', body })
    },
    async delete(id: string): Promise<void> {
      await request<void>(`${base}/${id}`, { method: 'DELETE' })
    }
  }
}

export { BASE_URL as BASE44_BASE_URL }
